import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texte court" },
  { value: "textarea", label: "Texte long" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Téléphone" },
  { value: "number", label: "Nombre" },
  { value: "date", label: "Date" },
  { value: "url", label: "URL" },
  { value: "select", label: "Liste déroulante" },
  { value: "radio", label: "Boutons radio" },
  { value: "checkbox", label: "Case à cocher (oui/non)" },
  { value: "checkboxes", label: "Cases multiples" },
];

const HAS_OPTIONS = new Set(["select", "radio", "checkboxes"]);

const slugify = (s) => (s || "").toString().toLowerCase().trim()
  .replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");

const fieldNameify = (s) => (s || "").toString().toLowerCase().trim()
  .replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");

export default function FormBuilder() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();
  const [f, setF] = useState({
    slug: "",
    title: "",
    description: "",
    fields: [],
    submit_label: "Envoyer",
    success_message: "Merci, votre message a bien été envoyé.",
    redirect_url: "",
    recipients: [],
    active: true,
  });
  const [recipientsText, setRecipientsText] = useState("");
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    api.get(`/admin/forms/${id}`).then(r => {
      const loaded = r.data;
      loaded.fields = (loaded.fields || []).map(fl => ({ ...fl, _uid: fl._uid || fieldUidRef.next() }));
      setF(loaded);
      setRecipientsText((loaded.recipients || []).join(", "));
      setLoading(false);
    }).catch(e => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editing]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const setField = (idx, k, v) => setF(prev => ({
    ...prev,
    fields: prev.fields.map((fl, i) => i === idx ? { ...fl, [k]: v } : fl),
  }));
  const addField = () => setF(prev => ({
    ...prev,
    fields: [...prev.fields, { _uid: fieldUidRef.next(), name: `champ_${prev.fields.length + 1}`, label: `Nouveau champ ${prev.fields.length + 1}`, type: "text", required: false }],
  }));
  const removeField = (idx) => setF(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }));
  const moveField = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= f.fields.length) return;
    const next = [...f.fields];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    set("fields", next);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!f.title.trim()) return toast.error("Titre obligatoire");
    if (!f.slug.trim()) return toast.error("Slug obligatoire");
    if (f.fields.length === 0) return toast.error("Ajoutez au moins un champ");
    // Validate unique field names
    const names = f.fields.map(x => x.name);
    if (new Set(names).size !== names.length) return toast.error("Les noms de champs doivent être uniques");

    const payload = {
      ...f,
      recipients: recipientsText.split(",").map(s => s.trim()).filter(Boolean),
      fields: f.fields.map(fl => {
        // eslint-disable-next-line no-unused-vars
        const { _uid, ...rest } = fl;
        return {
          ...rest,
          options: HAS_OPTIONS.has(fl.type)
            ? (Array.isArray(fl.options) ? fl.options : (fl.options || "").split("\n").map(s => s.trim()).filter(Boolean))
            : null,
        };
      }),
    };

    setSaving(true);
    try {
      if (editing) {
        const { slug, ...patch } = payload;
        await api.put(`/admin/forms/${id}`, patch);
      } else {
        await api.post("/admin/forms", payload);
      }
      toast.success("Enregistré");
      nav("/admin/formulaires");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12 text-center font-mono">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <form onSubmit={save} className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <Link to="/admin/formulaires" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</Link>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{editing ? "Modifier le formulaire" : "Nouveau formulaire"}</h1>
          <button type="submit" disabled={saving} data-testid="save-form-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Settings */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white border border-slate-900/15 p-5 space-y-3">
              <h2 className="font-display font-bold text-sm uppercase tracking-widest">Paramètres</h2>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Titre *</span>
                <input value={f.title} onChange={e => set("title", e.target.value)} onBlur={() => !editing && !f.slug && set("slug", slugify(f.title))} className="input mt-1" placeholder="Demande de devis" data-testid="form-title-input"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Slug (URL) *</span>
                <input value={f.slug} onChange={e => set("slug", slugify(e.target.value))} disabled={editing} className="input mt-1 font-mono" placeholder="demande-devis" data-testid="form-slug-input"/>
                <span className="text-xs text-slate-400">URL publique : /f/{f.slug || "…"}</span>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Description</span>
                <textarea value={f.description || ""} onChange={e => set("description", e.target.value)} rows={3} className="input mt-1" placeholder="Affichée en haut du formulaire"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Texte du bouton</span>
                <input value={f.submit_label} onChange={e => set("submit_label", e.target.value)} className="input mt-1"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Message de succès</span>
                <textarea value={f.success_message} onChange={e => set("success_message", e.target.value)} rows={2} className="input mt-1"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Redirection (optionnel)</span>
                <input value={f.redirect_url || ""} onChange={e => set("redirect_url", e.target.value)} placeholder="https://…" className="input mt-1"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Emails destinataires</span>
                <input value={recipientsText} onChange={e => setRecipientsText(e.target.value)} placeholder="contact@…, jean@…" className="input mt-1"/>
                <span className="text-xs text-slate-400">Séparés par des virgules.</span>
              </label>
              <label className="flex items-center gap-2 text-sm pt-2 border-t border-slate-200">
                <input type="checkbox" checked={f.active} onChange={e => set("active", e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="form-active-toggle"/>
                Formulaire actif (publiquement accessible)
              </label>
            </div>
          </div>

          {/* Fields */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-sm uppercase tracking-widest">Champs ({f.fields.length})</h2>
              <button type="button" onClick={addField} data-testid="add-field-btn" className="btn-brutal bg-white border border-slate-900 px-3 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-1">
                <Plus className="w-3 h-3"/> Ajouter un champ
              </button>
            </div>

            {f.fields.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">
                Aucun champ pour le moment. Cliquez sur « Ajouter un champ ».
              </div>
            )}

            {f.fields.map((fl, idx) => (
              <div key={fl._uid || `field-${idx}`} className="bg-white border border-slate-900/15 p-4 space-y-2" data-testid={`field-row-${idx}`}>
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-slate-400"/>
                  <input value={fl.label} onChange={e => setField(idx, "label", e.target.value)} onBlur={() => !fl.name && setField(idx, "name", fieldNameify(fl.label))} placeholder="Libellé visible" className="input flex-1"/>
                  <select value={fl.type} onChange={e => setField(idx, "type", e.target.value)} className="input w-44">
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveField(idx, -1)} className="p-1 border border-slate-300 hover:bg-slate-100" disabled={idx === 0}><ArrowUp className="w-3 h-3"/></button>
                    <button type="button" onClick={() => moveField(idx, 1)} className="p-1 border border-slate-300 hover:bg-slate-100" disabled={idx === f.fields.length - 1}><ArrowDown className="w-3 h-3"/></button>
                  </div>
                  <button type="button" onClick={() => removeField(idx)} title="Supprimer" className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 className="w-3 h-3"/></button>
                </div>
                <div className="grid md:grid-cols-3 gap-2">
                  <input value={fl.name} onChange={e => setField(idx, "name", fieldNameify(e.target.value))} placeholder="nom_technique" className="input font-mono text-xs"/>
                  <input value={fl.placeholder || ""} onChange={e => setField(idx, "placeholder", e.target.value)} placeholder="Placeholder" className="input"/>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={fl.required || false} onChange={e => setField(idx, "required", e.target.checked)} className="w-4 h-4 accent-slate-900"/>
                    Obligatoire
                  </label>
                </div>
                <input value={fl.help_text || ""} onChange={e => setField(idx, "help_text", e.target.value)} placeholder="Texte d'aide (affiché sous le champ)" className="input"/>
                {HAS_OPTIONS.has(fl.type) && (
                  <textarea
                    value={Array.isArray(fl.options) ? fl.options.join("\n") : (fl.options || "")}
                    onChange={e => setField(idx, "options", e.target.value.split("\n"))}
                    rows={3}
                    placeholder="Une option par ligne"
                    className="input font-mono text-sm"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </form>
      <Footer />
    </div>
  );
}
